"use client"
import { useState, useEffect } from "react"
import { Modal, Button } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"
import Select from "react-select"
import { getDiseasesAction } from "../../../../../store/actions/DiseasesAction"
import { updateLeadAction, addLeadAddressInfoAction } from "../../../../../store/actions/LeadAction.js"
import countryStateCity from "../countriesStatesCities.json"

const LeadPersonalModal = ({ isOpen, onClose, formData: propFormData, onSubmit }) => {
  const [formData, setFormData] = useState({
    first_name: "",
    middle_name: "",
    last_name: "",
    Contact: "",
    email: "",
    gender: "",
    idNumber: "",
    Diseases: [], // Changed to array for multi-select
    // Address fields
    tempAddressLine1: "",
    tempAddressLine2: "",
    tempArea: "",
    tempCity: "",
    tempState: "",
    tempCountry: "",
    tempPincode: "",
    permAddressLine1: "",
    permAddressLine2: "",
    permArea: "",
    permCity: "",
    permState: "",
    permCountry: "",
    permPincode: "",
    useSameAddress: false,
  })

  const [selectedDiseases, setSelectedDiseases] = useState([]) // Changed to array
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)

  // Address-related state
  const [countries, setCountries] = useState([])
  const [states, setStates] = useState([])
  const [cities, setCities] = useState([])
  const [permStates, setPermStates] = useState([])
  const [permCities, setPermCities] = useState([])

  const dispatch = useDispatch()
  const diseases = useSelector((state) => state.diseases.diseases || [])

  useEffect(() => {
    dispatch(getDiseasesAction(false))
  }, [dispatch])

  // Load countries
  useEffect(() => {
    const countryList = countryStateCity.map((country) => ({
      value: country.name,
      label: country.name,
    }))
    setCountries(countryList)
  }, [])

  // Load states based on temp country
  useEffect(() => {
    if (formData.tempCountry) {
      const selectedCountry = countryStateCity.find((c) => c.name === formData.tempCountry)
      const stateOptions =
        selectedCountry?.states?.map((state) => ({
          value: state.name,
          label: state.name,
        })) || []
      setStates(stateOptions)
      // Reset temp state and city if country changes
      if (formData.tempState && !stateOptions.find((s) => s.value === formData.tempState)) {
        setFormData((prev) => ({
          ...prev,
          tempState: "",
          tempCity: "",
        }))
      }
    } else {
      setStates([])
      setFormData((prev) => ({
        ...prev,
        tempState: "",
        tempCity: "",
      }))
    }
  }, [formData.tempCountry])

  // Load cities based on temp state
  useEffect(() => {
    if (formData.tempCountry && formData.tempState) {
      const selectedCountry = countryStateCity.find((c) => c.name === formData.tempCountry)
      const selectedState = selectedCountry?.states?.find((s) => s.name === formData.tempState)
      const cityOptions =
        selectedState?.cities?.map((city) => ({
          value: city.name,
          label: city.name,
        })) || []
      setCities(cityOptions)
      // Reset temp city if state changes and current city is not in new state
      if (formData.tempCity && !cityOptions.find((c) => c.value === formData.tempCity)) {
        setFormData((prev) => ({
          ...prev,
          tempCity: "",
        }))
      }
    } else {
      setCities([])
      setFormData((prev) => ({
        ...prev,
        tempCity: "",
      }))
    }
  }, [formData.tempCountry, formData.tempState])

  // Load states based on permanent country
  useEffect(() => {
    if (formData.permCountry) {
      const selectedCountry = countryStateCity.find((c) => c.name === formData.permCountry)
      const stateOptions =
        selectedCountry?.states?.map((state) => ({
          value: state.name,
          label: state.name,
        })) || []
      setPermStates(stateOptions)
      // Reset perm state and city if country changes
      if (formData.permState && !stateOptions.find((s) => s.value === formData.permState)) {
        setFormData((prev) => ({
          ...prev,
          permState: "",
          permCity: "",
        }))
      }
    } else {
      setPermStates([])
      setFormData((prev) => ({
        ...prev,
        permState: "",
        permCity: "",
      }))
    }
  }, [formData.permCountry])

  // Load cities based on permanent state
  useEffect(() => {
    if (formData.permCountry && formData.permState) {
      const selectedCountry = countryStateCity.find((c) => c.name === formData.permCountry)
      const selectedState = selectedCountry?.states?.find((s) => s.name === formData.permState)
      const cityOptions =
        selectedState?.cities?.map((city) => ({
          value: city.name,
          label: city.name,
        })) || []
      setPermCities(cityOptions)
      // Reset perm city if state changes and current city is not in new state
      if (formData.permCity && !cityOptions.find((c) => c.value === formData.permCity)) {
        setFormData((prev) => ({
          ...prev,
          permCity: "",
        }))
      }
    } else {
      setPermCities([])
      setFormData((prev) => ({
        ...prev,
        permCity: "",
      }))
    }
  }, [formData.permCountry, formData.permState])

  // Updated useEffect to properly map the data
  useEffect(() => {
    if (isOpen && propFormData) {
      console.log("PropFormData received:", propFormData) // Debug log
      // Parse the name field properly
      const nameParts = (propFormData.name || "").split(" ")
      const firstName = nameParts[0] || ""
      const middleName = nameParts.length > 2 ? nameParts.slice(1, -1).join(" ") : ""
      const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : ""

      setFormData({
        first_name: propFormData.first_name || firstName,
        middle_name: propFormData.middle_name || propFormData.middile_name || middleName,
        last_name: propFormData.last_name || lastName,
        Contact: propFormData.Contact || propFormData.whatsapp_contact_no || "",
        email: propFormData.email || propFormData.patient_email || "",
        gender: propFormData.gender || "",
        idNumber: propFormData.idNumber || propFormData.id_number || "",
        Diseases: propFormData.Diseases || propFormData.disease_names || [],
        date_of_birth: propFormData.date_of_birth || propFormData.dob || "",

        // Address fields - Fixed mapping
        tempAddressLine1: propFormData.tempAddressLine1 || propFormData.TemporaryAddress?.address_line1 || "",
        tempAddressLine2: propFormData.tempAddressLine2 || propFormData.TemporaryAddress?.address_line2 || "",
        tempArea: propFormData.tempArea || propFormData.TemporaryAddress?.area || "",
        tempCity: propFormData.tempCity || propFormData.TemporaryAddress?.city || "",
        tempState: propFormData.tempState || propFormData.TemporaryAddress?.state || "",
        tempCountry: propFormData.tempCountry || propFormData.TemporaryAddress?.country || "",
        tempPincode: propFormData.tempPincode || propFormData.TemporaryAddress?.pincode || "",
        permAddressLine1:
          propFormData.permAddressLine1 ||
          propFormData.PermanentAddress?.address_line1 ||
          propFormData.addressLine1 ||
          "",
        permAddressLine2:
          propFormData.permAddressLine2 ||
          propFormData.PermanentAddress?.address_line2 ||
          propFormData.addressLine2 ||
          "",
        permArea: propFormData.permArea || propFormData.PermanentAddress?.area || propFormData.area || "",
        permCity: propFormData.permCity || propFormData.PermanentAddress?.city || propFormData.city || "",
        permState: propFormData.permState || propFormData.PermanentAddress?.state || propFormData.state || "",
        permCountry: propFormData.permCountry || propFormData.PermanentAddress?.country || propFormData.country || "",
        permPincode: propFormData.permPincode || propFormData.PermanentAddress?.pincode || propFormData.pincode || "",
        useSameAddress: false,
      })

      // Handle disease selection - Fixed disease mapping
      if (diseases.length > 0) {
        let diseaseArray = []
        // Handle different disease data formats
        if (propFormData.diseases && Array.isArray(propFormData.diseases)) {
          diseaseArray = propFormData.diseases.map((d) => d.disease_name)
        } else if (propFormData.Disease) {
          diseaseArray = propFormData.Disease.split(" | ").filter((d) => d.trim())
        } else if (propFormData.Diseases) {
          diseaseArray = Array.isArray(propFormData.Diseases) ? propFormData.Diseases : [propFormData.Diseases]
        }

        console.log("Disease array:", diseaseArray) // Debug log

        const matchedDiseases = diseases
          .filter((d) => diseaseArray.includes(d.disease_name))
          .map((d) => ({ value: d.disease_id, label: d.disease_name }))

        console.log("Matched diseases:", matchedDiseases) // Debug log
        setSelectedDiseases(matchedDiseases)
      }
    }
  }, [isOpen, propFormData, diseases])
  useEffect(() => {
    if (formData.useSameAddress) {
      setFormData((prev) => ({
        ...prev,
        permAddressLine1: prev.tempAddressLine1,
        permAddressLine2: prev.tempAddressLine2,
        permArea: prev.tempArea,
        permCity: prev.tempCity,
        permState: prev.tempState,
        permCountry: prev.tempCountry,
        permPincode: prev.tempPincode,
      }));
    }
  }, [
    formData.useSameAddress,
    formData.tempAddressLine1,
    formData.tempAddressLine2,
    formData.tempArea,
    formData.tempCity,
    formData.tempState,
    formData.tempCountry,
    formData.tempPincode,
  ]);

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validatePhone = (phone) => {
    const phoneRegex = /^[0-9]{10}$/
    return phoneRegex.test(phone.replace(/\D/g, ""))
  }

  const validateForm = () => {
    const newErrors = {}

    // Personal Info validation
    if (!formData.first_name?.trim()) {
      newErrors.first_name = "First name is required"
    } else if (formData.first_name.trim().length < 2) {
      newErrors.first_name = "First name must be at least 2 characters"
    } else if (!/^[a-zA-Z\s]+$/.test(formData.first_name.trim())) {
      newErrors.first_name = "First name should contain only letters"
    }

    if (!formData.last_name?.trim()) {
      newErrors.last_name = "Last name is required"
    } else if (formData.last_name.trim().length < 2) {
      newErrors.last_name = "Last name must be at least 2 characters"
    } else if (!/^[a-zA-Z\s]+$/.test(formData.last_name.trim())) {
      newErrors.last_name = "Last name should contain only letters"
    }

    if (!formData.email?.trim()) {
      newErrors.email = "Email is required"
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address"
    }

    if (!formData.Contact?.trim()) {
      newErrors.Contact = "Contact number is required"
    } else if (!validatePhone(formData.Contact)) {
      newErrors.Contact = "Please enter a valid 10-digit phone number"
    }

    if (!formData.gender) {
      newErrors.gender = "Gender is required"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }))

    // Clear field error when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const handleSelectChange = (name, selectedOption) => {
    const value = selectedOption?.value || ""
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))

    // Clear field error
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }
  const handleUseSameAddressChange = (e) => {
    const checked = e.target.checked;
    setFormData((prev) => {
      if (checked) {
        return {
          ...prev,
          useSameAddress: true,
          permAddressLine1: prev.tempAddressLine1,
          permAddressLine2: prev.tempAddressLine2,
          permArea: prev.tempArea,
          permCity: prev.tempCity,
          permState: prev.tempState,
          permCountry: prev.tempCountry,
          permPincode: prev.tempPincode,
        };
      } else {
        return {
          ...prev,
          useSameAddress: false, // ✅ allows unchecking properly
        };
      }
    });
  };



  const handleSave = async () => {
    if (!validateForm()) {
      return
    }

    setLoading(true)
    try {
      // Prepare lead data for update
      const leadUpdateData = {
        lead_id: propFormData.lead_id || propFormData.id,
        first_name: formData.first_name,
        middle_name: formData.middle_name,
        last_name: formData.last_name,
        whatsapp_contact_no: formData.Contact,
        patient_email: formData.email,
        gender: formData.gender,
        id_number: formData.idNumber,
        date_of_birth: propFormData.date_of_birth || propFormData.dob || "",
        height: propFormData.height || "",
        weight: propFormData.weight || "",
        occupation: propFormData.occupation || "",
        id_proof: propFormData.id_proof || propFormData.idProof || "",
        lead_photo: propFormData.lead_photo || "",
        disease_names: selectedDiseases.map((d) => String(d.value)), // Fixed: Use disease IDs as strings
      }

      // Update lead personal information
      await dispatch(updateLeadAction(leadUpdateData))

      // Update address information if lead_id exists - Fixed the address structure
      if (propFormData.lead_id || propFormData.id) {
        const temporary_address = {
          addressLine1: formData.tempAddressLine1 || "", // Fixed field name
          addressLine2: formData.tempAddressLine2 || "", // Fixed field name
          area: formData.tempArea || "",
          city: formData.tempCity || "",
          state: formData.tempState || "",
          country: formData.tempCountry || "",
          pincode: formData.tempPincode || "",
        }

        const permanent_address = {
          addressLine1: formData.permAddressLine1 || "", // Fixed field name
          addressLine2: formData.permAddressLine2 || "", // Fixed field name
          area: formData.permArea || "",
          city: formData.permCity || "",
          state: formData.permState || "",
          country: formData.permCountry || "",
          pincode: formData.permPincode || "",
          useTemporaryAsPermanent: formData.useSameAddress,
        }

        await dispatch(
          addLeadAddressInfoAction({
            lead_id: propFormData.lead_id || propFormData.id,
            temporary_address: JSON.stringify(temporary_address),
            permanent_address: JSON.stringify(permanent_address),
          }),
        )
      }

      // Call the parent onSubmit with updated data
      onSubmit({
        ...formData,
        // Map back to parent component field names
        name: `${formData.first_name} ${formData.middle_name} ${formData.last_name}`.replace(/\s+/g, " ").trim(),
        whatsapp_contact_no: formData.Contact,
        patient_email: formData.email,
        id_number: formData.idNumber,
        Disease: selectedDiseases.map((d) => d.label).join(" | "),
        diseases: selectedDiseases.map((d) => ({ disease_name: d.label })),
        // Address fields mapped back
        tempAddressLine1: formData.tempAddressLine1,
        tempAddressLine2: formData.tempAddressLine2,
        tempArea: formData.tempArea,
        tempCity: formData.tempCity,
        tempState: formData.tempState,
        tempCountry: formData.tempCountry,
        tempPincode: formData.tempPincode,
        addressLine1: formData.permAddressLine1,
        addressLine2: formData.permAddressLine2,
        area: formData.permArea,
        city: formData.permCity,
        state: formData.permState,
        country: formData.permCountry,
        pincode: formData.permPincode,
        // Also update the nested address objects
        TemporaryAddress: {
          address_line1: formData.tempAddressLine1,
          address_line2: formData.tempAddressLine2,
          area: formData.tempArea,
          city: formData.tempCity,
          state: formData.tempState,
          country: formData.tempCountry,
          pincode: formData.tempPincode,
        },
        PermanentAddress: {
          address_line1: formData.permAddressLine1,
          address_line2: formData.permAddressLine2,
          area: formData.permArea,
          city: formData.permCity,
          state: formData.permState,
          country: formData.permCountry,
          pincode: formData.permPincode,
        },
      })
      onClose()
    } catch (error) {
      console.error("Error updating lead information:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setErrors({})
    setLoading(false)
    onClose()
  }

  const handleDiseasesChange = (selectedOptions) => {
    setSelectedDiseases(selectedOptions || [])
  }

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "45px",
      fontSize: "15px",
      borderRadius: "0.375rem",
      border: state.isFocused ? "1px solid #86b7fe" : "1px solid #ced4da",
      boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)" : "none",
      "&:hover": {
        borderColor: state.isFocused ? "#86b7fe" : "#ced4da",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 12px",
    }),
    input: (base) => ({
      ...base,
      margin: "0",
      paddingTop: "0",
      paddingBottom: "0",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: "8px",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
    }),
  }

  return (
    <Modal show={isOpen} onHide={handleClose} centered size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Edit Lead Information</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        <form>
          {/* Personal Information Section */}
          <div className="mb-4">
            <h6 className="text-primary mb-3">Personal Information</h6>
            {/* Name Fields Row */}
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  First Name <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="text"
                  name="first_name"
                  className={`form-control ${errors.first_name ? "is-invalid" : ""}`}
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder="Enter first name"
                  style={{ height: "45px" }}
                />
                {errors.first_name && <div className="invalid-feedback">{errors.first_name}</div>}
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Middle Name</label>
                <input
                  type="text"
                  name="middle_name"
                  className="form-control"
                  value={formData.middle_name}
                  onChange={handleChange}
                  placeholder="Enter middle name"
                  style={{ height: "45px" }}
                />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Last Name <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="text"
                  name="last_name"
                  className={`form-control ${errors.last_name ? "is-invalid" : ""}`}
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder="Enter last name"
                  style={{ height: "45px" }}
                />
                {errors.last_name && <div className="invalid-feedback">{errors.last_name}</div>}
              </div>
            </div>

            {/* Contact and Email Row */}
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Phone Number <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="text"
                  name="Contact"
                  className={`form-control ${errors.Contact ? "is-invalid" : ""}`}
                  value={formData.Contact}
                  onChange={handleChange}
                  placeholder="Enter phone number"
                  maxLength="10"
                  style={{ height: "45px" }}
                />
                {errors.Contact && <div className="invalid-feedback">{errors.Contact}</div>}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Email <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  className={`form-control ${errors.email ? "is-invalid" : ""}`}
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  style={{ height: "45px" }}
                />
                {errors.email && <div className="invalid-feedback">{errors.email}</div>}
              </div>
            </div>

            {/* Gender and ID Number Row */}
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Gender <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  name="gender"
                  className={`form-select ${errors.gender ? "is-invalid" : ""}`}
                  value={formData.gender}
                  onChange={handleChange}
                  style={{ height: "45px" }}
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Transgender">Transgender</option>
                </select>
                {errors.gender && <div className="invalid-feedback">{errors.gender}</div>}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Date of Birth</label>
                <div className="form-group position-relative">
                  <input
                    type="date"
                    className="form-control"
                    name="date_of_birth"
                    value={formData.date_of_birth || ""}
                    onChange={handleChange}
                    max={new Date().toISOString().split("T")[0]}
                    style={{ height: "45px" }}
                  />
                  <i
                    className="fa fa-calendar position-absolute"
                    style={{
                      right: "15px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      pointerEvents: "none",
                      fontSize: "18px",
                      color: "#6c757d",
                    }}
                  ></i>
                </div>
              </div>

            </div>

            {/* Disease Row - Multi-select */}
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">ID Number</label>
                <input
                  type="text"
                  name="idNumber"
                  className="form-control"
                  value={formData.idNumber}
                  onChange={handleChange}
                  placeholder="Enter ID number"
                  style={{ height: "45px" }}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <label className="form-label">
                  Diseases<span style={{ color: "red" }}>*</span>
                </label>
                {/* <Select
                  isMulti
                  options={diseases.map((d) => ({
                    value: d.disease_id,
                    label: d.disease_name,
                  }))}
                  value={selectedDiseases}
                  onChange={handleDiseasesChange}
                  placeholder="Select Diseases"
                  className="basic-multi-select"
                  classNamePrefix="select"
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "50px",
                    }),
                    multiValue: (provided) => ({
                      ...provided,
                      backgroundColor: "#e3f2fd",
                      color: "#1976d2",
                    }),
                    multiValueLabel: (provided) => ({
                      ...provided,
                      color: "#1976d2",
                      fontWeight: "500",
                    }),
                    dropdownIndicator: (provided) => ({
                      ...provided,
                      padding: "4px",
                    }),
                    indicatorSeparator: (provided) => ({
                      ...provided,
                      display: "none",
                    }),
                  }}
                /> */}
                <Select
  isMulti={false}
  options={diseases.map((d) => ({
    value: d.disease_id,
    label: d.disease_name,
  }))}
  value={selectedDiseases}
  onChange={(option) => setSelectedDiseases(option ? [option] : [])}
  placeholder="Select Disease"
  className="basic-single-select"
  classNamePrefix="select"
  styles={{
    control: (provided) => ({
      ...provided,
      minHeight: "50px",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "4px",
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: "none",
    }),
  }}
/>

              </div>
            </div>
          </div>

          {/* Temporary Address Section */}
          <div className="mb-4">
            <h6 className="text-primary mb-3">Temporary Address</h6>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Address Line 1</label>
                <input
                  type="text"
                  name="tempAddressLine1"
                  className="form-control"
                  value={formData.tempAddressLine1}
                  onChange={handleChange}
                  placeholder="Enter address line 1"
                  style={{ height: "45px" }}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Address Line 2</label>
                <input
                  type="text"
                  name="tempAddressLine2"
                  className="form-control"
                  value={formData.tempAddressLine2}
                  onChange={handleChange}
                  placeholder="Enter address line 2"
                  style={{ height: "45px" }}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Area</label>
                <input
                  type="text"
                  name="tempArea"
                  className="form-control"
                  value={formData.tempArea}
                  onChange={handleChange}
                  placeholder="Enter area"
                  style={{ height: "45px" }}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Country</label>
                <Select
                  options={countries}
                  value={countries.find((c) => c.value === formData.tempCountry) || null}
                  onChange={(option) => handleSelectChange("tempCountry", option)}
                  placeholder="Select a Country"
                  styles={selectStyles}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">State</label>
                <Select
                  options={states}
                  value={states.find((s) => s.value === formData.tempState) || null}
                  onChange={(option) => handleSelectChange("tempState", option)}
                  placeholder="Select a State"
                  styles={selectStyles}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">City</label>
                <Select
                  options={cities}
                  value={cities.find((c) => c.value === formData.tempCity) || null}
                  onChange={(option) => handleSelectChange("tempCity", option)}
                  placeholder="Select a City"
                  styles={selectStyles}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Pincode</label>
                <input
                  type="text"
                  name="tempPincode"
                  className="form-control"
                  value={formData.tempPincode}
                  onChange={handleChange}
                  placeholder="Enter pincode"
                  pattern="[0-9]{6}"
                  maxLength="6"
                  style={{ height: "45px" }}
                />
              </div>
            </div>
          </div>

          {/* Permanent Address Section */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="text-primary mb-0">Permanent Address</h6>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="useSameAddress"
                  checked={formData.useSameAddress}
                  onChange={handleUseSameAddressChange}
                />
                <label className="form-check-label" htmlFor="useSameAddress">
                  Same as temporary address
                </label>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Address Line 1</label>
                <input
                  type="text"
                  name="permAddressLine1"
                  className="form-control"
                  value={formData.permAddressLine1}
                  onChange={handleChange}
                  placeholder="Enter address line 1"
                  disabled={formData.useSameAddress}
                  style={{ height: "45px" }}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Address Line 2</label>
                <input
                  type="text"
                  name="permAddressLine2"
                  className="form-control"
                  value={formData.permAddressLine2}
                  onChange={handleChange}
                  placeholder="Enter address line 2"
                  disabled={formData.useSameAddress}
                  style={{ height: "45px" }}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Area</label>
                <input
                  type="text"
                  name="permArea"
                  className="form-control"
                  value={formData.permArea}
                  onChange={handleChange}
                  placeholder="Enter area"
                  disabled={formData.useSameAddress}
                  style={{ height: "45px" }}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Country</label>
                <Select
                  options={countries}
                  value={countries.find((c) => c.value === formData.permCountry) || null}
                  onChange={(option) => handleSelectChange("permCountry", option)}
                  placeholder="Select a Country"
                  styles={selectStyles}
                  isDisabled={formData.useSameAddress}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">State</label>
                <Select
                  options={permStates}
                  value={permStates.find((s) => s.value === formData.permState) || null}
                  onChange={(option) => handleSelectChange("permState", option)}
                  placeholder="Select a State"
                  styles={selectStyles}
                  isDisabled={formData.useSameAddress}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">City</label>
                <Select
                  options={permCities}
                  value={permCities.find((c) => c.value === formData.permCity) || null}
                  onChange={(option) => handleSelectChange("permCity", option)}
                  placeholder="Select a City"
                  styles={selectStyles}
                  isDisabled={formData.useSameAddress}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Pincode</label>
                <input
                  type="text"
                  name="permPincode"
                  className="form-control"
                  value={formData.permPincode}
                  onChange={handleChange}
                  placeholder="Enter pincode"
                  pattern="[0-9]{6}"
                  maxLength="6"
                  disabled={formData.useSameAddress}
                  style={{ height: "45px" }}
                />
              </div>
            </div>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="danger" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={loading}>
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
              Updating...
            </>
          ) : (
            "Save Changes"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default LeadPersonalModal
